import { useEditorStore } from '@/store/editorStore';
import type { Command } from './types';

function selectedNodeId(ctx: { editorState: { selectedNodeIds: string[] } }): string | undefined {
  return ctx.editorState.selectedNodeIds[0];
}

export const nodeCommands: Command[] = [
  {
    id: 'node.addChild',
    label: 'Add Child Node',
    category: 'node',
    canExecute: (ctx) => !!selectedNodeId(ctx),
    execute: (ctx) => {
      const parentId = selectedNodeId(ctx);
      if (!parentId) return;
      const newId = ctx.doc.addChild(parentId);
      ctx.editorState.selectNode(newId);
      ctx.editorState.startEditing(newId);
      useEditorStore.getState().setIsNewNode(true);
    },
  },
  {
    id: 'node.addChildAbove',
    label: 'Add Child Above',
    category: 'node',
    canExecute: (ctx) => !!selectedNodeId(ctx),
    execute: (ctx) => {
      const parentId = selectedNodeId(ctx);
      if (!parentId) return;
      const newId = ctx.doc.addChild(parentId, '', 'start');
      ctx.editorState.selectNode(newId);
      ctx.editorState.startEditing(newId);
      useEditorStore.getState().setIsNewNode(true);
    },
  },
  {
    id: 'node.addSiblingBelow',
    label: 'Add Sibling Below',
    category: 'node',
    canExecute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (!id) return false;
      const node = ctx.doc.getNode(id);
      return !!node?.parentId;
    },
    execute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (!id) return;
      const newId = ctx.doc.addSiblingAfter(id);
      if (newId) {
        ctx.editorState.selectNode(newId);
        ctx.editorState.startEditing(newId);
        useEditorStore.getState().setIsNewNode(true);
      }
    },
  },
  {
    id: 'node.addSiblingAbove',
    label: 'Add Sibling Above',
    category: 'node',
    canExecute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (!id) return false;
      const node = ctx.doc.getNode(id);
      return !!node?.parentId;
    },
    execute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (!id) return;
      const newId = ctx.doc.addSiblingBefore(id);
      if (newId) {
        ctx.editorState.selectNode(newId);
        ctx.editorState.startEditing(newId);
        useEditorStore.getState().setIsNewNode(true);
      }
    },
  },
  {
    id: 'node.delete',
    label: 'Delete Node',
    category: 'node',
    canExecute: (ctx) => {
      const ids = ctx.editorState.selectedNodeIds;
      if (ids.length === 0) return false;
      const rootId = ctx.doc.getMeta().rootNodeId;
      // At least one non-root node must be selected
      return ids.some((id) => id !== rootId);
    },
    execute: (ctx) => {
      const meta = ctx.doc.getMeta();
      const nodeIds = [...ctx.editorState.selectedNodeIds];
      // Track a parent to select after deletion
      let lastParentId: string | null = null;
      // Delete in reverse order to avoid parent-child ordering issues
      for (const id of nodeIds.reverse()) {
        if (id !== meta.rootNodeId) {
          const node = ctx.doc.getNode(id);
          if (node?.parentId) lastParentId = node.parentId;
          ctx.doc.deleteNode(id);
        }
      }
      // Select last known parent or clear
      if (lastParentId) {
        ctx.editorState.selectNode(lastParentId);
      } else {
        ctx.editorState.clearSelection();
      }
    },
  },
  {
    id: 'node.editText',
    label: 'Edit Node Text',
    category: 'node',
    canExecute: (ctx) => !!selectedNodeId(ctx),
    execute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (id) ctx.editorState.startEditing(id);
    },
  },
  {
    id: 'node.stopEditing',
    label: 'Stop Editing',
    category: 'node',
    canExecute: (ctx) => ctx.editorState.mode === 'editing',
    execute: (ctx) => {
      ctx.editorState.stopEditing();
    },
  },
  {
    id: 'node.moveUp',
    label: 'Move Node Up',
    category: 'node',
    canExecute: (ctx) => !!selectedNodeId(ctx),
    execute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (id) ctx.doc.moveNodeUp(id);
    },
  },
  {
    id: 'node.moveDown',
    label: 'Move Node Down',
    category: 'node',
    canExecute: (ctx) => !!selectedNodeId(ctx),
    execute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (id) ctx.doc.moveNodeDown(id);
    },
  },
  {
    id: 'node.toggleCollapse',
    label: 'Toggle Collapse',
    category: 'node',
    canExecute: (ctx) => !!selectedNodeId(ctx),
    execute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (id) ctx.doc.toggleCollapse(id);
    },
  },
  {
    id: 'node.resetPosition',
    label: 'Reset to Auto-Layout',
    category: 'node',
    canExecute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (!id) return false;
      const node = ctx.doc.getNode(id);
      return !!node?.position && !!node.parentId;
    },
    execute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (id) ctx.doc.clearNodePosition(id);
    },
  },
  {
    id: 'node.resetSize',
    label: 'Reset Size',
    category: 'node',
    canExecute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (!id) return false;
      const node = ctx.doc.getNode(id);
      return !!node?.sizeOverride;
    },
    execute: (ctx) => {
      const id = selectedNodeId(ctx);
      if (id) ctx.doc.clearNodeSize(id);
    },
  },
  {
    id: 'node.resetAllPositions',
    label: 'Reset All to Auto-Layout',
    category: 'node',
    execute: (ctx) => {
      ctx.doc.clearAllPositionOverrides();
    },
  },
];
